import { FormattedJsonDisplay } from "#src/components/FormattedJsonDisplay/FormattedJsonDisplay";
import {
  KeyValueList,
  KeyValueListLayout,
  RichTextMarkdownDataDisplay,
  type KeyValuePairList,
} from "@validereinc/common-components";
import {
  CallWriteApiMetadataSchema,
  ExecuteWorkflowMetadataSchema,
  PendingApprovalMetadataSchema,
  PendingApprovalToolName,
  type CallWriteApiMetadataType,
  type ExecuteWorkflowMetadataType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useMemo } from "react";
import {
  formatInvestigationWriteApiDisplayName,
  formatMarkdownInlineCode,
  formatWriteApiPathWithQueryParams,
} from "./ApprovalCardContent.helpers";
import styles from "./PendingApprovalCard.module.scss";

const cx = classNames.bind(styles);

const APPROVAL_JSON_COPY_MESSAGE = "Copied approval details to your clipboard.";

const buildCallWriteApiKeyValueRows = (
  metadata: CallWriteApiMetadataType
): KeyValuePairList => {
  const rows: KeyValuePairList = [
    {
      title: "API",
      value: formatInvestigationWriteApiDisplayName(metadata.api),
    },
    {
      title: "Method",
      value: (
        <RichTextMarkdownDataDisplay
          value={formatMarkdownInlineCode(metadata.method)}
          isInline
          isContainerSizing
          isConstrainedToParentWidth
          className={cx(
            "pending-approval-card__field-value",
            "pending-approval-card__field-markdown"
          )}
        />
      ),
    },
    {
      title: "Path",
      value: (
        <RichTextMarkdownDataDisplay
          value={formatMarkdownInlineCode(
            formatWriteApiPathWithQueryParams(metadata.path, metadata.params)
          )}
          isInline
          isContainerSizing
          isConstrainedToParentWidth
          className={cx(
            "pending-approval-card__field-value",
            "pending-approval-card__field-markdown"
          )}
        />
      ),
    },
  ];

  if (metadata.body !== undefined) {
    rows.push({
      title: "Body",
      value: (
        <FormattedJsonDisplay
          data={metadata.body}
          isFullWidth
          copySuccessMessage={APPROVAL_JSON_COPY_MESSAGE}
        />
      ),
    });
  }

  return rows;
};

const hasExecuteWorkflowStartInput = (
  startInput: ExecuteWorkflowMetadataType["start_input"]
): boolean => {
  if (startInput === null || startInput === undefined) {
    return false;
  }

  return Object.keys(startInput).length > 0;
};

const buildExecuteWorkflowKeyValueRows = (
  metadata: ExecuteWorkflowMetadataType
): KeyValuePairList => {
  const rows: KeyValuePairList = [
    {
      title: "Name",
      value: metadata.name,
    },
    {
      title: "Category",
      value: metadata.category_name,
    },
    {
      title: "Configuration",
      value: (
        <FormattedJsonDisplay
          data={metadata.config}
          isFullWidth
          copySuccessMessage={APPROVAL_JSON_COPY_MESSAGE}
        />
      ),
    },
  ];

  if (hasExecuteWorkflowStartInput(metadata.start_input)) {
    rows.push({
      title: "Start input",
      value: (
        <FormattedJsonDisplay
          data={metadata.start_input}
          isFullWidth
          copySuccessMessage={APPROVAL_JSON_COPY_MESSAGE}
        />
      ),
    });
  }

  return rows;
};

export type JsonApprovalCardContentProps = {
  data: unknown;
};

/**
 * Fallback approval card content: pretty-printed JSON with copy support.
 */
export const JsonApprovalCardContent = ({
  data,
}: JsonApprovalCardContentProps) => (
  <FormattedJsonDisplay
    data={data}
    copySuccessMessage={APPROVAL_JSON_COPY_MESSAGE}
  />
);

export type CallWriteApiApprovalCardContentProps = {
  metadata: CallWriteApiMetadataType;
};

/**
 * Approval card content for the investigation `call_write_api` tool.
 */
export const CallWriteApiApprovalCardContent = ({
  metadata,
}: CallWriteApiApprovalCardContentProps) => {
  const keyValueRows = useMemo(
    () => buildCallWriteApiKeyValueRows(metadata),
    [metadata]
  );

  return (
    <KeyValueList
      data={keyValueRows}
      layout={KeyValueListLayout.COMPACT}
      variant="plain"
      valueAlignment="left"
      className={cx("pending-approval-card__key-value-list")}
    />
  );
};

export type ExecuteWorkflowApprovalCardContentProps = {
  metadata: ExecuteWorkflowMetadataType;
};

export const ExecuteWorkflowApprovalCardContent = ({
  metadata,
}: ExecuteWorkflowApprovalCardContentProps) => {
  const keyValueRows = useMemo(
    () => buildExecuteWorkflowKeyValueRows(metadata),
    [metadata]
  );

  return (
    <KeyValueList
      data={keyValueRows}
      layout={KeyValueListLayout.COMPACT}
      variant="plain"
      valueAlignment="left"
      className={cx("pending-approval-card__key-value-list")}
    />
  );
};

/**
 * Resolves pending-approval card body content from message metadata.
 * Returns `null` when metadata is missing so `PendingApprovalCard` can show its fallback copy.
 */
export const resolveApprovalCardContent = (
  metadata: unknown
): React.JSX.Element | null => {
  if (metadata === null || metadata === undefined) {
    return null;
  }

  const parsedEnvelope = PendingApprovalMetadataSchema.safeParse(metadata);
  if (!parsedEnvelope.success) {
    return <JsonApprovalCardContent data={metadata} />;
  }

  const { tool_name, tool_metadata } = parsedEnvelope.data;

  if (tool_name === PendingApprovalToolName.CALL_WRITE_API) {
    const parsedWriteMetadata =
      CallWriteApiMetadataSchema.safeParse(tool_metadata);
    if (!parsedWriteMetadata.success) {
      return <JsonApprovalCardContent data={tool_metadata} />;
    }

    return (
      <CallWriteApiApprovalCardContent metadata={parsedWriteMetadata.data} />
    );
  }

  if (tool_name === PendingApprovalToolName.EXECUTE_WORKFLOW) {
    const parsedExecuteMetadata =
      ExecuteWorkflowMetadataSchema.safeParse(tool_metadata);
    if (!parsedExecuteMetadata.success) {
      return <JsonApprovalCardContent data={tool_metadata} />;
    }

    return (
      <ExecuteWorkflowApprovalCardContent
        metadata={parsedExecuteMetadata.data}
      />
    );
  }

  return <JsonApprovalCardContent data={tool_metadata} />;
};
